import React, { FC, useEffect, useRef, useState } from "react";
import { FieldValues, useFormContext, useWatch } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiGrid,
  MuiMultiSelect,
  MuiRadioGroup,
  MuiSelect,
  MuiSelectPhoneWithTextInput,
  MuiSelectWithTextInput,
  OptionsDataType,
} from "../../../../../components";
import {
  MAX_LENGTH_100,
  MAX_LENGTH_15,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import {
  isAllParticipants,
  isMyCaseLoads,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import {
  ParticipantsSearchFormKey,
  SEARCH_TYPE_KEY,
} from "../../../modelsAndSchemas/Models";
import { searchParticipantsSearchSectionSiteCoreKeys } from "../../../sitecore/SiteCoreKeys";
import { ParticipantsSearchFormWrapper } from "./ParticipantsSearchForm.style";

interface ParticipantsSearchFormProps extends FieldValues {
  onMyCaseLoadsRadioToggle: () => void;
  handleSaveFilter: () => void;
  coachTypeData: OptionsDataType[];
  coachesList: OptionsDataType[];
  makeMobileFieldFocussed?: boolean;
}
const pageId = PAGE_IDS.SEARCH_PARTICIPANTS;

export const ParticipantsSearchForm: FC<ParticipantsSearchFormProps> = ({
  onMyCaseLoadsRadioToggle,
  handleSaveFilter,
  coachTypeData,
  coachesList,
  makeMobileFieldFocussed = false,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { setValue, getValues, control, watch } = useFormContext();
  const [resetForm, setResetForm] = useState(false);
  const {
    commonLabels,
    fetchDropdownValues,
    useFetchLabel,
    fetchValidationMessage,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const coachIdsRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const participantStatusRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);

  const formValues = useWatch({ control });
  const coachIdFormValues = useWatch({
    name: ParticipantsSearchFormKey.coachIds,
    control,
  });

  // While disabling the save btn - empty form - Fields to ignore in the check
  const ignoredFields = [
    ParticipantsSearchFormKey.searchType,
    ParticipantsSearchFormKey.resetForm,
    ParticipantsSearchFormKey.participantFirstNameFilter,
    ParticipantsSearchFormKey.participantLastNameFilter,
    ParticipantsSearchFormKey.keyNumberFilter,
    ParticipantsSearchFormKey.participantEmailFilter,
    ParticipantsSearchFormKey.clientNameFilter,
  ] as string[];

  // While disabling the save btn - empty form - Check if any of the fields have values in them
  const isValidForm = Object.entries(formValues).some(([key, value]) => {
    return (
      !ignoredFields.includes(key) &&
      !Array.isArray(value) &&
      value?.trim() !== ""
    );
  });

  const {
    searchParticipantsSearchSectionTitle,
    myCaseloadsRadioButton,
    selectSearchTypeHeader,
    allParticipantsRadioButton,
    participantFirstName,
    participantFirstNamePlaceholderText,
    participantLastName,
    participantLastNamePlaceholderText,
    programKey,
    programKeyPlaceholderText,
    participantEmail,
    participantEmailPlaceholderText,
    participantMobileNumber,
    clientName,
    clientNamePlaceholderText,
    coach,
    ageingLabel,
    ageingList,
    filterByList,
    coachTypeDropDown,
    participantStatus,
    participantStatusList,
  } = searchParticipantsSearchSectionSiteCoreKeys;

  const getLabel = useFetchLabel(searchParticipantsSearchSectionTitle);

  const filterDropdownValue = fetchDropdownValues(
    searchParticipantsSearchSectionTitle,
    filterByList,
  );

  const agingDropdownValue = fetchDropdownValues(
    searchParticipantsSearchSectionTitle,
    ageingList,
  );

  const radioOptions = [
    {
      value: SEARCH_TYPE_KEY.myCaseLoads,
      label: getLabel(myCaseloadsRadioButton),
    },
    {
      value: SEARCH_TYPE_KEY.allParticipants,
      label: getLabel(allParticipantsRadioButton),
    },
  ];

  const handleSearchTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchType = e.target.value;
    setValue(ParticipantsSearchFormKey.searchType, searchType);
    onMyCaseLoadsRadioToggle();
  };

  useEffect(() => {
    if (
      isNotEmpty(coachIdFormValues) &&
      coachIdsRef?.current &&
      isAllParticipants(formValues.searchType)
    ) {
      coachIdsRef.current.resetValues(coachIdFormValues);
    }
    if (
      isNotEmpty(formValues.participantStatus) &&
      participantStatusRef?.current &&
      isAllParticipants(formValues.searchType)
    ) {
      participantStatusRef.current.resetValues(formValues.participantStatus);
    }
  }, [coachIdFormValues, formValues.searchType, formValues.participantStatus]);

  useEffect(() => {
    setResetForm(!resetForm);
  }, [watch(ParticipantsSearchFormKey.resetForm)]);

  return (
    <ParticipantsSearchFormWrapper data-testid="participants-search-form-wrapper">
      <MuiBox>
        <FormController
          control={control}
          name={ParticipantsSearchFormKey.searchType}
        >
          <MuiRadioGroup
            label={getLabel(selectSearchTypeHeader)}
            value={getValues(ParticipantsSearchFormKey.searchType)}
            onChange={handleSearchTypeChange}
            options={radioOptions}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiBox>
      <MuiGrid
        container
        className="form-container"
        data-testid="participants-search-form-grid"
      >
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={ParticipantsSearchFormKey.participantFirstName}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(participantFirstName)}
              placeholder={getLabel(participantFirstNamePlaceholderText)}
              control={control}
              selectBoxName={
                ParticipantsSearchFormKey.participantFirstNameFilter
              }
              id={`${pageId}-${ParticipantsSearchFormKey.participantFirstName}`}
              selectBoxId={`${pageId}-${ParticipantsSearchFormKey.participantFirstNameFilter}`}
              maxLength={MAX_LENGTH_50}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={ParticipantsSearchFormKey.participantLastName}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(participantLastName)}
              placeholder={getLabel(participantLastNamePlaceholderText)}
              control={control}
              selectBoxName={
                ParticipantsSearchFormKey.participantLastNameFilter
              }
              id={`${pageId}-${ParticipantsSearchFormKey.participantLastName}`}
              selectBoxId={`${pageId}-${ParticipantsSearchFormKey.participantLastNameFilter}`}
              maxLength={MAX_LENGTH_50}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={ParticipantsSearchFormKey.keyNumber}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(programKey)}
              placeholder={getLabel(programKeyPlaceholderText)}
              control={control}
              selectBoxName={ParticipantsSearchFormKey.keyNumberFilter}
              id={`${pageId}-${ParticipantsSearchFormKey.keyNumber}`}
              selectBoxId={`${pageId}-${ParticipantsSearchFormKey.keyNumberFilter}`}
              maxLength={MAX_LENGTH_15}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={ParticipantsSearchFormKey.participantEmail}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(participantEmail)}
              placeholder={getLabel(participantEmailPlaceholderText)}
              control={control}
              selectBoxName={ParticipantsSearchFormKey.participantEmailFilter}
              id={`${pageId}-${ParticipantsSearchFormKey.participantEmail}`}
              selectBoxId={`${pageId}-${ParticipantsSearchFormKey.participantEmailFilter}`}
              maxLength={MAX_LENGTH_50}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={ParticipantsSearchFormKey.participantMobileNumber}
          >
            <MuiSelectPhoneWithTextInput
              ignoreError
              label={getLabel(participantMobileNumber)}
              defaultValue={getValues(
                ParticipantsSearchFormKey.participantMobileNumber,
              )}
              defaultSelectedCountry={getValues(
                ParticipantsSearchFormKey.mobileISOCode,
              )}
              placeholder={commonLabels.telephonePlaceHolder}
              phoneNumberFormId={
                ParticipantsSearchFormKey.participantMobileNumber
              }
              countryCodeFormId={ParticipantsSearchFormKey.mobileCountryCode}
              countryIsoCodeFormId={ParticipantsSearchFormKey.mobileISOCode}
              reset={resetForm}
              withSelectOption
              searchPlaceholder={commonLabels.search}
              searchNotFound={commonLabels.noResult}
              selectLabel={commonLabels.selectPlaceHolder}
              invalidPhoneNumberMessage={fetchValidationMessage(
                commonSiteCoreKeys.invalidPhoneNumber,
              )}
              makeFocus={makeMobileFieldFocussed}
            />
          </FormController>
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeLargeClass}>
          <FormController
            control={control}
            name={ParticipantsSearchFormKey.clientName}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(clientName)}
              placeholder={getLabel(clientNamePlaceholderText)}
              control={control}
              selectBoxName={ParticipantsSearchFormKey.clientNameFilter}
              id={`${pageId}-${ParticipantsSearchFormKey.clientName}`}
              selectBoxId={`${pageId}-${ParticipantsSearchFormKey.clientNameFilter}`}
              maxLength={MAX_LENGTH_100}
              requiredARIALabel={commonARIALabel.required}
              tooltipMessage={
                commonValidationMessages.fieldSpecialCharactersTooltipMessage
              }
              tooltipHeaderTitle={
                commonValidationMessages.allowedSpecialCharactersTitle
              }
            />
          </FormController>
        </MuiGrid>
        {isAllParticipants(getValues(ParticipantsSearchFormKey.searchType)) && (
          <>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ParticipantsSearchFormKey.coachType}
              >
                <MuiSelect
                  label={getLabel(coachTypeDropDown)}
                  options={coachTypeData}
                  visibleDefaultSelectOption
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ParticipantsSearchFormKey.coachIds}
                ref={coachIdsRef}
              >
                <MuiMultiSelect
                  id={`${pageId}-${ParticipantsSearchFormKey.coachIds}`}
                  label={getLabel(coach)}
                  options={coachesList}
                  showSearch
                  showChip
                  chipId={`${pageId}-${ParticipantsSearchFormKey.coachIds}-chip`}
                  dialogTitle={getLabel(coach)}
                  isDialogTitleWithEllipsis={false}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>

            <MuiGrid size={gridSize} className={gridSizeLargeClass}>
              <FormController
                control={control}
                name={ParticipantsSearchFormKey.participantStatus}
                ref={participantStatusRef}
              >
                <MuiMultiSelect
                  label={getLabel(participantStatus)}
                  options={fetchDropdownValues(
                    searchParticipantsSearchSectionTitle,
                    participantStatusList,
                  )}
                  showSearch
                  showChip
                  id={`${pageId}-${ParticipantsSearchFormKey.participantStatus}`}
                  chipId={`${pageId}-${ParticipantsSearchFormKey.participantStatus}-chip`}
                  dialogTitle={getLabel(participantStatus)}
                  selectPlaceHolder={commonLabels.selectPlaceHolder}
                  searchPlaceHolder={commonLabels.search}
                  selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                  doneText={commonLabels.done}
                  allARIAProps={multiSelectARIALabels}
                />
              </FormController>
            </MuiGrid>
          </>
        )}

        {isMyCaseLoads(getValues(ParticipantsSearchFormKey.searchType)) && (
          <MuiGrid size={gridSize} className={gridSizeLargeClass}>
            <FormController
              control={control}
              name={ParticipantsSearchFormKey.ageing}
            >
              <MuiSelect
                label={getLabel(ageingLabel)}
                options={agingDropdownValue}
                visibleDefaultSelectOption
                id={`${pageId}-${ParticipantsSearchFormKey.ageing}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        )}
      </MuiGrid>

      <MuiGrid container className="button-container">
        <MuiButtonSecondary
          onClick={handleSaveFilter}
          id={`${pageId}-saveFilter`}
          data-testid={`${pageId}-saveFilter`}
          disabled={!isValidForm}
        >
          {commonLabels.saveFilter}
        </MuiButtonSecondary>
        <MuiButtonPrimary
          id={`${pageId}-search`}
          data-testid={`${pageId}-search`}
          type="submit"
        >
          {commonLabels.search}
        </MuiButtonPrimary>
      </MuiGrid>
    </ParticipantsSearchFormWrapper>
  );
};
